import React, {useEffect, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {useDocumentTitle, useUnfinished} from "../hooks";

import {getUnfinishedPageSettings} from "../api/setting/pageApi";
import getPageBreadcrumb from "../services/BreadcrumbServices";
import {setPageSettings} from "../store/actions/pageActions";
import BackendLayout from "../layouts/BackendLayout";
import {REGISTER_TREATMENT, UNFINISHED_ACTION_LIST} from "../constants/routes";
import {ButtonDPad, ButtonFilter, DatetimePicker, FormGroup, Label, PageTitle, RedirectAlert} from "../components";
import PageAlert from "../components/alerts/PageAlert";
import Select2 from "../components/Select2";
import {
  getChildGroupOptionConfig,
  getGroupAndMachines,
  getSelectedOption
} from "../utils/group-option-utils";
import { ArrayUtils, errorStatusHandle, hasErrorInput } from "../utils";
import Table from "../components/table/Table";
import Pagination from "../components/Pagination";
import {useLocation, useNavigate} from "react-router-dom";
import {resetAlert, setAlertSuccess, setPageLoading} from "../store/actions";
import {changeOrder} from "../utils/common-utils";
import LoadingSpinner from "../components/loading/LoadingSpinner";
import { USER_SETTING_CATEGORIES } from "../constants/app";
import { updateUserSetting} from "../services/user-master/UserSettingServices"
import {UNFINISHED_ACTION_LIST_PAGE} from "../constants/pageName";
import useRedirectPage from "../hooks/useRedirectPage";

export default function UnfinishedActionListPage() {
  useRedirectPage(UNFINISHED_ACTION_LIST_PAGE);
  useDocumentTitle("処置未完了リスト - 点検王");
  const dispatch = useDispatch();
  const breadcrumb = getPageBreadcrumb(UNFINISHED_ACTION_LIST);

  const [reload, setReload] = useState(0);
  const initialFilter = () => {
    if (location.state) return location.state;
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth()-1);
    const startDateDay = (startDate.getDate()).toString().padStart(2, '0');
    const startDateMonth = startDate.getMonth() + 1;
    const startDateYear = startDate.getFullYear();

    const endDate = new Date();
    const endDateDay = (endDate.getDate()).toString().padStart(2, '0');
    const endDateMonth = endDate.getMonth() + 1;
    const endDateYear = endDate.getFullYear();
    return {
      registration_start_date: `${startDateYear}/${(startDateMonth).toString().padStart(2, '0')}/${startDateDay}`,
      registration_end_date: `${endDateYear}/${endDateMonth.toString().padStart(2, '0')}/${endDateDay}`
    }
  };
  const navigate = useNavigate();
  const location = useLocation();
  const groupSettings = useSelector((state) => state.app_settings.machine_group);
  const [filterList, setFilterList] = useState(initialFilter);
  const [groupsOptions, setGroupOptions] = useState({});
  const OPTION_ALL = {value: "", label:'[ALL]'};
  const BUTTON_SEARCH_UNFINISHED = 'search-unfinished';
  const { unfinished, currentPage, pageSize, totalCount, orderList, errors,
    setCurrentPage, setPageSize, setFilters, setOrderList } = useUnfinished(reload, filterList, BUTTON_SEARCH_UNFINISHED);
  const [groupsList, setGroupsList] = useState({});
  const [groupsChildren, setGroupsChildren] = useState({});
  const [machines, setMachines] = useState([]);

  useEffect(() => {
    dispatch(setPageLoading());
    getUnfinishedPageSettings().then((pageSettings) => {
      dispatch(setPageSettings(pageSettings));
      if (!location.state && pageSettings.user_setting?.setting) {
          setFilterList(pageSettings.user_setting.setting);
          setFilters(pageSettings.user_setting.setting);
      }
      setReload(reload + 1);
      const {groupListOptions, groupAndChildrenOptions} = getGroupAndMachines(pageSettings.options);
      setMachines(pageSettings.options?.machines);
      setGroupsList(groupListOptions);
      setGroupsChildren(groupAndChildrenOptions);
    }).catch(e => {
      errorStatusHandle(e.response);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const successMessage = localStorage.getItem('success_message');
    dispatch(setAlertSuccess(successMessage));
    localStorage.removeItem('success_message');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let groupCombine = {1: filterList['group_1'], 2: filterList['group_2'], 3: filterList['group_3'], 4: filterList['group_4'], 5: filterList['group_5']};
    const options = getChildGroupOptionConfig(groupsChildren, groupCombine, groupsList, machines, true);
    setGroupOptions(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterList, groupsChildren, groupsList]);

  const updateSettings = async () => {
    try {
      // eslint-disable-next-line no-unused-vars
      const response = await updateUserSetting(filterList, USER_SETTING_CATEGORIES.UNFINISH_LIST);
    } catch (e) {
      errorStatusHandle(e);
    }
  };

  // Function to handle column sorting
  const handleSortColumn = (column) => {
    let currentOrder = changeOrder(orderList[column]);
    setOrderList({ ...orderList, [column]: currentOrder });
  };

  const handleChangeFilter = (e, fieldName) => {
    if (fieldName.startsWith('group_')) {
      const updateFilter = { ...filterList};
      let x = fieldName.split("_").pop();
      x = parseInt(x);
      updateFilter[fieldName] = e.value;
      for (let i = 1; i <= 5; i++) {
        if (groupSettings["group_" + i].active) {
          if (i < x) {
            updateFilter["group_" + i] = updateFilter["group_" + i];
          } else if (i > x) {
            updateFilter["group_" + i] = "";
          }
        }
      }
      updateFilter["machine_id"] = "";
      setFilterList(updateFilter);
    } else {
      setFilterList({ ...filterList, [fieldName]: e.value});
    }
  };

  const handleSubmitFilter = (e) => {
    dispatch(resetAlert());
    updateSettings();
    setFilters({ ...filterList });
    setReload(reload + 1);
  };

  const onChangeDate = (name, newDate) => {
    setFilterList(currentData => ({ ...currentData, [name]: newDate }));
  };

  const getDateFormat = (dateTime) => {
    if (dateTime === null) return "-";
    const datePart = dateTime.split('T');
    const [year, month, day] = datePart[0].split('-');
    return `${year}/${month}/${day}`;
  };

  const getUserFormat = (data) => {
    if (data.last_treatment_name === null) return "-";

    let result = data.last_treatment_name;
    if (data.last_treatment_username) {
      result = result + " (" + data.last_treatment_username + ")";
    }
    return result;
  };

  //Function help display error
  const hasError = (names) => {
    let results = false;
    if (errors === undefined || errors.length === 0) {
      return false;
    }
    // eslint-disable-next-line array-callback-return
    names.map((name, idx) => {
      if (hasErrorInput(errors, name) === true) {
        results = true;
      }
    });
    return results;
  };

  const urlTreatment  = (inspectionId) => REGISTER_TREATMENT.replace(':inspectionId', inspectionId);
  const handleRedirect = (inspectionId) => {
    navigate(urlTreatment(inspectionId), {state: {type: "unfinishedAction", data: filterList}})
  };

  return (
    <BackendLayout breadcrumb={breadcrumb} sidebar={true}>
      <PageTitle title="処置未完了リスト"/>
      <ButtonDPad />
      <div className="grid gap-x-8 py-[20px] max-w-[1460px]">
        <div className="inline-block pr-[24px]">
          <RedirectAlert />
          <PageAlert />
          <div className="bg-white rounded-lg pt-2 xl:pt-4 border border-[#e0e0e0]">
            <span className="2xl:text-[18px] xl:text-[16px] md:text-[15px] font-light px-[16px]">条件を選択してください</span>
            <div className="2xl:mt-[10px] xl:mt-[10px] md:mt-[8px] flex items-center">
              <FormGroup direction="vertical" className="xl:px-[16px] px-2 w-fit">
                <Label text={groupSettings.machine?.name} htmlFor="machine_id" className="mb-[4px] whitespace-nowrap"/>
                <Select2 className="w-full md:min-w-[150px] xl:min-w-[175px] md:text-[14px] xl:text-[16px]"
                         classNamePrefix="react-select"
                         options={groupsOptions?.machines}
                         isClearable={false} maxLength={200}
                         value={getSelectedOption(groupsOptions?.machines, filterList['machine_id'], OPTION_ALL)}
                         name="machine_id"
                         nextFocus='group_1_id'
                         onChange={(e) => handleChangeFilter(e, 'machine_id')} />
              </FormGroup>
              {ArrayUtils.range(1, 5).map((i, idx) =>
                groupSettings[`group_${i}`]?.active && (
                  <FormGroup direction="vertical" className="xl:px-[16px] px-2 w-fit" key={idx}>
                    <Label text={groupSettings[`group_${i}`].name} htmlFor={`group_${i}`} className="mb-[4px] whitespace-nowrap"/>
                    <Select2 className="w-full md:min-w-[150px] xl:min-w-[175px] md:text-[14px] xl:text-[16px]"
                             classNamePrefix="react-select"
                             isClearable={false} maxLength={30}
                             options={groupsOptions[i] || []}
                             nextFocus={groupSettings[`group_${i + 1}`]?.active ? `group_${parseInt(i + 1)}_id` : 'registration_start_date'}
                             value={getSelectedOption(groupsOptions[i], filterList[`group_${i}`], OPTION_ALL)}
                             name={`group_${i}`}
                             onChange={(e) => handleChangeFilter(e, `group_${i}`)}
                             autoFocus={i === 1} />
                  </FormGroup>
                )
              )}

              <FormGroup direction="vertical" className="xl:px-[16px] px-2">
                <Label className="font-medium mb-[4px]" text="点検結果登録日"/>
                <div className="flex items-center relative">
                  <DatetimePicker id="registration_start_date"
                                  name="registration_start_date"
                                  value={filterList['registration_start_date']}
                                  maxLength={15}
                                  timeFormat={false}
                                  onChange={onChangeDate}
                                  nextFocus='registration_end_date'
                                  className="w-full md:w-[130px] xl:w-[150px] md:text-[14px] xl:text-[16px]"
                                  hasError={hasError(['registration_start_date', 'registration_date'])}/>
                  <span className="2xl:text-[26px] xl:text-[22px] md:text-[22px] xl:px-[16px] px-2">~</span>
                  <DatetimePicker name="registration_end_date"
                                  id="registration_end_date"
                                  value={filterList['registration_end_date']}
                                  maxLength={15}
                                  timeFormat={false}
                                  onChange={onChangeDate}
                                  className="w-full md:w-[130px] xl:w-[150px] md:text-[14px] xl:text-[16px]"
                                  hasError={hasError(['registration_end_date', 'registration_date'])} />
                </div>
              </FormGroup>
              <FormGroup direction="vertical" className="xl:px-[16px] px-2 mr-24">
                <Label className="h-[24px] mb-[4px]"/>
                <ButtonFilter onClick={handleSubmitFilter} id={BUTTON_SEARCH_UNFINISHED} className="min-w-[160px]"/>
              </FormGroup>
            </div>
          </div>
          <div className="mt-[16px] overflow-x-auto relative min-w-[970px] border border-[#e0e0e0] rounded-[8px]">
            <Table striped>
              <Table.Head className="">
                <Table.HeadCell className="bg-tenken-gray-300 text-tenken-tablet-regular xl:text-base min-w-[60px]">
                  #
                </Table.HeadCell>
                <Table.HeadCell className="normal-case px-2 lg:px-4 w-[200px]"
                                sortable
                                order={orderList['machine']}
                                onChangeOrder={() => handleSortColumn('machine')}>
                  <span>{groupSettings.machine?.name}</span>
                </Table.HeadCell>
                {ArrayUtils.range(1, 5).map((i, index) =>
                  groupSettings[`group_${i}`]?.active && (
                    <Table.HeadCell sortable key={index}
                                    className="px-2 lg:px-4"
                                    order={orderList[`group_${i}`]}
                                    onChangeOrder={() => handleSortColumn(`group_${i}`)}>
                      <span>{groupSettings[`group_${i}`]?.name}</span>
                    </Table.HeadCell>
                  )
                )}
                <Table.HeadCell sortable
                                className="min-w-[172px] px-2 lg:px-4"
                                order={orderList['registration_date']}
                                onChangeOrder={() => handleSortColumn('registration_date')}>
                  <span>点検結果登録日</span>
                </Table.HeadCell>
                <Table.HeadCell sortable
                                className="w-[240px] px-2 lg:px-4"
                                order={orderList['last_treatment_user']}
                                onChangeOrder={() => handleSortColumn('last_treatment_user')}>
                  <span>最終処置担当者</span>
                </Table.HeadCell>
                <Table.HeadCell sortable
                                className="min-w-[172px] px-2 lg:px-4"
                                order={orderList['last_treatment_date']}
                                onChangeOrder={() => handleSortColumn('last_treatment_date')}>
                  <span>最終処置日付</span>
                </Table.HeadCell>
              </Table.Head>
              {unfinished.length > 0 && (
                <Table.Body className="text-base  text-gray-900">
                  <LoadingSpinner component="unfinished-action-list" />
                  {unfinished.map((row, index) => (
                    <Table.Row key={index}>
                      <Table.Cell className="px-2 lg:px-4">{((currentPage - 1) * pageSize + 1) + index}</Table.Cell>
                      <Table.Cell className="px-2 lg:px-4 max-w-[200px] truncate text-[#2374fd] hover:text-[#2374fd]">
                        <span title={row.machine?.name}
                              className="cursor-pointer text-[16px] hover:underline"
                              onClick={() => handleRedirect(row.id)}>{row.machine?.name}</span>
                      </Table.Cell>
                      {ArrayUtils.range(1, 5).map((i, index) =>
                        groupSettings[`group_${i}`]?.active && (
                          <Table.Cell className="px-2 lg:px-4 max-w-[200px] truncate" key={index}>{row.machine.group_relation[`group_${i}`]?.name ?? '指定なし'}</Table.Cell>
                        )
                      )}
                      <Table.Cell className="px-2 lg:px-4">{getDateFormat(row.registration_datetime)}</Table.Cell>
                      <Table.Cell className="px-2 lg:px-4 max-w-[240px] truncate">{getUserFormat(row) || '-'}</Table.Cell>
                      <Table.Cell className="px-2 lg:px-4">{getDateFormat(row.last_treatment_date)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              )}
            </Table>
          </div>
          <div className="pt-[16px]">
            {unfinished.length > 0 ?
              <Pagination showIndex={totalCount > 0}
                          setPage={setCurrentPage}
                          currentPage={currentPage}
                          pageSize={pageSize}
                          setPageSize={setPageSize}
                          totalCount={totalCount}/>
              : <p>該当する検索結果が見つかりませんでした。別の検索条件をお試しください。</p>
            }
          </div>
        </div>
      </div>
    </BackendLayout>
  );
}
